/**
 * native AbortController that also aborts when an optional parent signal aborts
 * @param parent parent AbortSignal to link to (optional)
 */
export function childAbortController(parent?: AbortSignal): AbortController {
  const ctrl = new AbortController()
  if (parent) {
    if (parent.aborted) {
      ctrl.abort(parent.reason)
    } else {
      const onParentAbort = () => ctrl.abort(parent.reason)
      parent.addEventListener('abort', onParentAbort, { once: true })
      // release the parent listener once this controller aborts for any reason
      ctrl.signal.addEventListener('abort', () => parent.removeEventListener('abort', onParentAbort), {
        once: true,
      })
    }
  }
  return ctrl
}

/**
 * the canonical abort error, recognised by `isAbortError()`
 */
export function abortError(): DOMException {
  return new DOMException('Aborted', 'AbortError')
}

/**
 * throw an AbortError if the signal is aborted
 */
export function throwIfAborted(signal: AbortSignal) {
  if (!signal.aborted) return
  // Always an AbortError, whatever `signal.reason` holds: the loaders' isAbortError() filters
  // (and Resource's catchAbortError on its deferred) rely on that shape. Reads `aborted` rather
  // than calling the native `throwIfAborted()`, which only exists from Safari 15.4 / Chrome 100 /
  // Firefox 97.
  throw abortError()
}

/**
 * wrap a promise, and a return a proxy promise wich resolve with the same value, but reject with AbortError if the signal is aborted
 * @param promise the promise to wrap
 * @param signal the abort signal to listen to
 * @returns
 */
export async function wrapAbort<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
  const val: T = await promise
  throwIfAborted(signal)
  return val
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function catchIfAbortError(e: any) {
  if (!isAbortError(e)) {
    throw e
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function catchAbortError<T = any>(p: Promise<T>): Promise<void | T> {
  return p.catch(catchIfAbortError)
}

/**
 * return true if error is AbortError
 * @param e
 * @returns
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isAbortError(e: any) {
  return e?.name === 'AbortError' || e?.message === 'Aborted'
}
